import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, FileText, Globe } from 'lucide-react';
import { appId, db } from '@/lib/firebase';

type ReadinessStatus = 'submitted' | 'in_review' | 'scanning' | 'completed';

interface ReadinessScanRecord {
  websiteUrl: string;
  websiteHost: string;
  email: string | null;
  source: string;
  status: ReadinessStatus;
  progressPercent: number;
}

interface FederalReadinessResultsPageProps {
  userId: string;
}

export function FederalReadinessResultsPage({
  userId,
}: FederalReadinessResultsPageProps): JSX.Element | null {
  const navigate = useNavigate();
  const [scanRecord, setScanRecord] = useState<ReadinessScanRecord | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const scanRef = doc(db, 'artifacts', appId, 'users', userId, 'federalReadinessScan', 'main');
    getDoc(scanRef).then((docSnap) => {
      setLoading(false);
      if (!docSnap.exists()) {
        navigate('/dashboard', { replace: true });
        return;
      }
      const data = docSnap.data() as Partial<ReadinessScanRecord>;
      const status = data.status as ReadinessStatus | undefined;
      if (
        !data.websiteUrl ||
        !data.websiteHost ||
        !data.source ||
        !status ||
        status !== 'completed'
      ) {
        navigate('/dashboard', { replace: true });
        return;
      }
      const progressPercent =
        typeof data.progressPercent === 'number'
          ? Math.max(0, Math.min(100, data.progressPercent))
          : 100;
      setScanRecord({
        websiteUrl: data.websiteUrl,
        websiteHost: data.websiteHost,
        email: data.email ?? null,
        source: data.source,
        status,
        progressPercent,
      });
    });
  }, [userId, navigate]);

  if (loading) {
    return (
      <main
        id="main-content"
        className="min-h-[60vh] flex items-center justify-center bg-gray-50"
        role="main"
        aria-label="Federal Readiness results"
      >
        <p className="text-smb-slate-600">Loading your results…</p>
      </main>
    );
  }

  if (!scanRecord) {
    return null;
  }

  return (
    <main
      id="main-content"
      className="bg-gray-50 font-sans text-gray-900"
      role="main"
      aria-label="Federal Readiness Scan results"
    >
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm font-medium text-smb-primary-mid hover:text-smb-primary mb-6"
        >
          <ArrowLeft size={16} aria-hidden="true" />
          Back to dashboard
        </Link>

        <div className="rounded-2xl border border-smb-slate-100 bg-white shadow-sm p-6 sm:p-8">
          <div className="flex items-center gap-3 text-smb-primary-mid">
            <div className="shrink-0 w-10 h-10 rounded-lg bg-smb-primary-light/20 flex items-center justify-center">
              <FileText size={22} className="text-smb-primary-mid" aria-hidden="true" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-smb-slate-600">
                Federal Readiness Scan
              </p>
              <h1 className="text-xl sm:text-2xl font-bold text-smb-ink">
                Your scan results
              </h1>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-smb-slate-100 bg-smb-slate-50/50 p-4 sm:p-5">
            <div className="flex items-center gap-2 text-smb-slate-600 mb-2">
              <Globe size={16} aria-hidden="true" />
              <span className="text-sm font-medium">Scanned website</span>
            </div>
            <a
              href={scanRecord.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-base font-semibold text-smb-primary-mid hover:text-smb-primary hover:underline"
            >
              {scanRecord.websiteHost}
            </a>
          </div>

          <div className="mt-4 flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-800 px-3 py-1.5 w-fit">
            <CheckCircle2 size={16} aria-hidden="true" />
            <span className="text-sm font-semibold">Scan completed</span>
          </div>

          <p className="mt-6 text-sm text-smb-slate-600">
            Your Federal Readiness Scan for this website has been completed. Detailed findings and
            recommendations are available from our team — we will follow up with you at the email
            associated with your account.
          </p>

          <div className="mt-8 pt-6 border-t border-smb-slate-100">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 rounded-lg bg-smb-primary-mid px-4 py-2.5 text-sm font-semibold text-white hover:bg-smb-primary transition-colors"
            >
              <ArrowLeft size={16} aria-hidden="true" />
              Return to dashboard
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
